import React from 'react';

import { Text } from '@reckon-web/text';
import { Flex } from '@reckon-web/flex';
import { Stack } from '@reckon-web/stack';
import { Inline } from '@reckon-web/inline';
import { useTheme } from '@reckon-web/theme';
import { LoadingDots } from '@reckon-web/loading';
import { IconButton } from '@reckon-web/button';
import { PlusIcon } from '@reckon-web/icon';
import { makeId } from '@reckon-web/utils';

import { TaskStatusEnum, TaskStatusEnumValue } from '../types/task-status-enum';
import { taskStatuses } from '../../../globals';
import { Task } from '../../../hooks/use-query-tasks';
import { useUpdateTaskStatus } from '../../../hooks/use-update-task-status';
import { DueDate } from '../../common/due-date';
import {
  IDraggable,
  IDroppable,
  Kanban,
  KanbanDraggable,
} from '../../common/kanban';
import { TaskStatusBadge } from '../../common/task-status-badge';
import { NamedUserAvatar } from '../../common/named-user-avatar';
import { WorkItemTitle } from '../helpers';
import { useVisibleProperties } from '../../common/visible-properties';
import { CreateTaskOptions } from '../../task/create-drawer';
import { JobStatusEnum } from '../../job/types/job-status-enum';

const statusTitleHeight = '42px';

interface DraggableType extends IDraggable {
  task: Task;
  locked?: boolean;
  onClick?: () => void;
  testId?: string;
}

interface DraggablesGroupType {
  id: string;
  job: NonNullable<Task['job']>;
  locked?: boolean;
  draggables: DraggableType[];
  onClick?: () => void;
  onClickCreateTask?: (options?: CreateTaskOptions) => void;
  testId?: string;
  children?: React.ReactNode;
}

interface DroppableType extends IDroppable<DraggableType> {
  status: TaskStatusEnumValue;
  draggablesGroups: DraggablesGroupType[];
  testId?: string;
}

export const DraggableCard: React.FC<DraggableType> = React.memo(
  ({
    task,
    locked,
    onClick,
    pending,
    testId = `kanban-task-card-${task.cloudId}`,
  }) => {
    const theme = useTheme();
    const [{ taskBoard: show }] = useVisibleProperties();

    return (
      <Stack
        gap="small"
        padding="medium"
        borderRadius="small"
        background="base"
        boxShadow="xsmall"
        data={{ testid: testId }}
        css={{
          // FIXME: GAP - Card component
          ':hover': {
            backgroundColor: theme.palette.background.muted,
          },
        }}
      >
        <Stack gap="xsmall">
          <WorkItemTitle
            name={task.name}
            locked={locked}
            onClick={onClick}
            testId={makeId(testId, 'title')}
          />
          {show.client && task.entity?.name && (
            <Text
              size="xsmall"
              color="muted"
              data={{ testid: makeId(testId, 'entity-name') }}
            >
              {task.entity.name}
            </Text>
          )}
        </Stack>
        {show.dueDate &&
          task.status === TaskStatusEnum.valuesEnum.Cancelled &&
          task.completedDate && (
            <DueDate
              prefix="Cancelled"
              date={task.completedDate}
              size="xsmall"
              testId={makeId(testId, 'cancelled')}
            />
          )}
        {show.dueDate &&
          task.status === TaskStatusEnum.valuesEnum.Done &&
          task.completedDate && (
            <DueDate
              prefix="Completed"
              date={task.completedDate}
              size="xsmall"
              testId={makeId(testId, 'completed')}
            />
          )}
        {show.dueDate &&
          task.status !== TaskStatusEnum.valuesEnum.Cancelled &&
          task.status !== TaskStatusEnum.valuesEnum.Done &&
          task.dueDate && (
            <DueDate
              prefix="Due"
              date={task.dueDate}
              showDueDays
              size="xsmall"
              testId={makeId(testId, 'due')}
            />
          )}
        {show.estimatedTime && task.estimateHoursDisplay && (
          <Inline alignY="center" gap="xsmall">
            <Text
              size="xsmall"
              weight="medium"
              data={{ testid: makeId(testId, 'hours') }}
            >
              {task.estimateHoursDisplay} hours
            </Text>
            <Text
              size="xsmall"
              weight="medium"
              color="dim"
              data={{ testid: makeId(testId, 'estimate') }}
            >
              (estimate)
            </Text>
          </Inline>
        )}
        {show.assigned && task.assignee && (
          <NamedUserAvatar
            user={task.assignee}
            avatarSize="xsmall"
            textSize="xsmall"
            testId={makeId(testId, 'avatar')}
          />
        )}
        {pending && (
          <LoadingDots
            label="pending"
            size="small"
            data-testid={makeId(testId, 'loading-dots')}
          />
        )}
      </Stack>
    );
  }
);

const DraggablesGroup: React.FC<DraggablesGroupType> = (props) => {
  const {
    job,
    locked,
    onClick,
    onClickCreateTask,
    children,
    testId = `kanban-task-group-${job.cloudId}`,
  } = props;

  const theme = useTheme();
  const [{ taskBoard: show }] = useVisibleProperties();

  /* TODO we need a testId on the column */
  return (
    <Flex
      direction="column"
      paddingBottom="small"
      borderRadius="small"
      css={{
        backgroundColor: theme.palette.background.shade,
      }}
      data={{ testid: testId }}
    >
      <Flex
        justifyContent="space-between"
        gap="xsmall"
        padding="small"
        css={{
          position: 'sticky',
          top: statusTitleHeight,
          backgroundColor: theme.palette.background.shade,
          borderTopRightRadius: theme.radii.small,
          borderTopLeftRadius: theme.radii.small,
        }}
      >
        <Flex direction="column" gap="xsmall">
          <WorkItemTitle
            name={job.name}
            locked={locked}
            onClick={onClick}
            size="xsmall"
            testId={makeId(testId, 'title')}
            weight="regular"
          />
          {show.debtor && job.client?.name && (
            <Text
              size="xsmall"
              color="muted"
              data={{ testid: makeId(testId, 'client-name') }}
            >
              {job.client.name}
            </Text>
          )}
        </Flex>

        {onClickCreateTask && (
          <IconButton
            label="Add task to job"
            icon={PlusIcon}
            tone="passive"
            weight="none"
            size="small"
            onClick={() => onClickCreateTask()}
            data-testid={makeId(testId, 'icon-button')}
          />
        )}
      </Flex>
      <Flex direction="column" gap="small" paddingX="small">
        {children}
      </Flex>
    </Flex>
  );
};

const reducer = (draggable: DraggableType, droppable: DroppableType) => ({
  ...draggable,
  task: {
    ...draggable.task,
    status: droppable.status,
  },
});

const Title = ({
  status,
  draggables,
  testId = `kanban-task-card-title`,
}: DroppableType) => {
  return (
    <Flex
      alignItems="center"
      gap="xsmall"
      padding="small"
      height={statusTitleHeight}
    >
      <TaskStatusBadge
        status={status}
        testId={makeId(testId, 'status-badge')}
      />
      <Text
        size="xsmall"
        weight="medium"
        color="dim"
        data={{ testid: makeId(testId, 'text') }}
      >
        ({draggables.length})
      </Text>
    </Flex>
  );
};
// Recommended performance optimisation - memoize to prevent unnecessary re-renders
const MemoisedTitle = React.memo(Title);

const Body: React.FC<DroppableType & { Draggable: KanbanDraggable }> = ({
  id,
  draggablesGroups,
  draggables,
  Draggable,
}) => {
  return (
    <Flex direction="column" gap="small">
      {draggablesGroups.map((draggablesGroup) => (
        <DraggablesGroup key={draggablesGroup.id} {...draggablesGroup}>
          {draggablesGroup.draggables.map((draggable) => (
            <Draggable
              key={draggable.id}
              draggableId={draggable.id}
              droppableId={id}
              index={draggables.indexOf(draggable)}
              isDragDisabled={draggable.locked}
              onClick={draggable.onClick}
            >
              {/* TODO Workflow-400 draggablecard default test id is okay, consider working a test id into this body component */}
              <DraggableCard key={draggable.id} {...draggable} />
            </Draggable>
          ))}
        </DraggablesGroup>
      ))}
    </Flex>
  );
};
// Recommended performance optimisation - memoize to prevent unnecessary re-renders
const MemoisedBody = React.memo(Body);

export const KanbanView: React.FC<{
  tasks: Task[];
  compare: (a: Task, b: Task) => number;
  onClickJob?: (jobId: string) => void;
  onClickTask?: (taskId: string) => void;
  onClickCreateTask?: (options?: CreateTaskOptions) => void;
  testId?: string;
}> = React.memo(
  ({
    tasks,
    compare,
    onClickJob,
    onClickTask,
    onClickCreateTask,
    testId = `kanban-view`,
  }) => {
    const [UpdateTaskStatusDialog, updateTaskStatus] = useUpdateTaskStatus();

    const draggables = React.useMemo<DraggableType[]>(
      () =>
        tasks.map((task) => ({
          id: task.cloudId || '',
          task,
          locked: task.job?.status.status === JobStatusEnum.valuesEnum.Closed,
          onClick: onClickTask && (() => onClickTask(task.cloudId || '')),
        })),
      [tasks, onClickTask]
    );

    const sort = React.useCallback<
      (draggables: DraggableType[]) => DroppableType[]
    >(
      (_draggables) =>
        taskStatuses.map(({ id: status }) => {
          const draggables = _draggables
            .filter(({ task }) => task.status === status)
            .sort(({ task: a }, { task: b }) => compare(a, b));

          const draggablesGroups = Object.values(
            draggables.reduce<{
              [id: string]: DraggablesGroupType;
            }>((o, draggable) => {
              const { task } = draggable;
              const jobId = task.jobId || '';
              let g = o[jobId];
              if (!g) {
                const { job } = task;
                if (!job) return o;
                g = {
                  id: job.cloudId || '',
                  job,
                  locked: job.status.status === JobStatusEnum.valuesEnum.Closed,
                  draggables: [],
                  onClick: onClickJob && (() => onClickJob(jobId)),
                  onClickCreateTask:
                    onClickCreateTask &&
                    (() => onClickCreateTask({ jobId: jobId, status })),
                };
                o[jobId] = g;
              }
              g.draggables.push(draggable);
              return o;
            }, {})
          );

          return {
            id: status,
            status,
            draggables,
            draggablesGroups,
          };
        }),
      [compare, onClickJob, onClickCreateTask]
    );

    const resolver = React.useCallback(
      (draggable: DraggableType, droppable: DroppableType) => {
        const { task } = draggable;
        const { status } = droppable;
        return updateTaskStatus({ task, status });
      },
      [updateTaskStatus]
    );

    return (
      <>
        <Kanban<DraggableType, DroppableType>
          draggables={draggables}
          sort={sort}
          reducer={reducer}
          resolver={resolver}
          Title={MemoisedTitle}
          Body={MemoisedBody}
          testId={testId}
        />
        <UpdateTaskStatusDialog />
      </>
    );
  }
);
